import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CreditCard, Check, Shield, Zap, Users, BarChart2, CheckCircle } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.asistentetradingia.com';

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || '';
}

const Subscription: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>('inactive');
  const [periodEnd, setPeriodEnd] = useState<string>('');
  const [cancelAtEnd, setCancelAtEnd] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchStatus();
    const urlStatus = searchParams.get('status');
    if (urlStatus === 'success') {
      setSuccess('¡Suscripción activada! Bienvenido a Premium.');
      fetchStatus();
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/stripe/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data.status || 'inactive');
        setPeriodEnd(data.subscription?.current_period_end || '');
        setCancelAtEnd(data.subscription?.cancel_at_period_end || false);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      setError('');

      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError('Error al crear la sesión de pago');
      }
    } catch (err: any) {
      setError('Error al procesar el pago. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      setError('');

      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/stripe/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        setError('Error al acceder al portal de gestión');
      }
    } catch (err: any) {
      setError('Error al acceder al portal. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: BarChart2, name: 'Análisis Ilimitados', description: 'Sin límites en el número de análisis diarios' },
    { icon: Shield, name: 'Análisis en Tiempo Real', description: 'Notificaciones push cuando el sistema detecta estructuras técnicas relevantes' },
    { icon: Users, name: 'Programa de Embajadores', description: 'Gana hasta un 50% de comision por referidos' },
    { icon: Zap, name: 'Conexión con Broker', description: 'Conecta tu cuenta para ver posiciones y journal automático' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Suscripción Premium</h1>
          <p className="mt-2 text-gray-400">
            Desbloquea todo el potencial de AsistenteTrading IA
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-900/20 border border-green-900/50 rounded-lg p-4 flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-green-500">{success}</p>
          </div>
        )}

        {status === 'active' && (
          <div className="card">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-900/20 rounded-lg">
                  <CreditCard className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Suscripción Activa</h2>
                  <p className="text-sm text-gray-400">
                    {cancelAtEnd
                      ? `Se cancela el ${new Date(periodEnd).toLocaleDateString('es-ES')}`
                      : `Próxima renovación: ${new Date(periodEnd).toLocaleDateString('es-ES')}`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleManageSubscription}
                disabled={loading}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
              >
                {loading ? 'Cargando...' : 'Gestionar'}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature) => (
            <div key={feature.name} className="card">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{feature.name}</h3>
                  <p className="mt-1 text-gray-400">{feature.description}</p>
                  {feature.name === 'Programa de Embajadores' && (
                    <p className="mt-1 text-xs text-gray-500">*Porcentaje calculado sobre el importe neto de la suscripcion, excluidos impuestos.</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {status !== 'active' && (
          <div className="card">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white">
                €49<span className="text-xl font-normal text-gray-400">/mes</span>
              </h2>
              <p className="mt-4 text-gray-400">Todo lo que necesitas para analizar el mercado con metodologia institucional</p>
              <ul className="mt-8 space-y-4">
                {features.map((feature) => (
                  <li key={feature.name} className="flex items-center justify-center text-gray-300">
                    <Check className="h-5 w-5 text-primary mr-2" />
                    {feature.name}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="mt-8 btn-primary w-full max-w-md"
              >
                {loading ? 'Redirigiendo a pago...' : 'Activar Premium — €49/mes'}
              </button>
              <p className="mt-3 text-xs text-gray-500">
                Cancela cuando quieras. Sin permanencia.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;
